import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Highlight,
    FeatureItem,
    FeatureTitle,
    FeaturesGrid,
    HeroSection,
    PageTitle,
    FigureWrapper,
    FigureCaption,
} from '../components/ui/PageElements';
import {
    FeatureList,
    FeatureListItem,
    TechSpecsList,
    TechSpecItem,
} from "../components/ui/ListStyles.jsx";
import ResponsiveImage from '../components/ui/ResponsiveImage';
import ResponsiveVideo from '../components/ui/ResponsiveVideo';
import PageMeta from '../components/PageMeta';
import { getProduct, getProductDownloads } from '../config/products';

const ProjectWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;

// Logo styling - uses ResponsiveImage with custom styling
const LogoWrapper = styled.div`
    max-width: 128px;
    margin: 0 auto 20px auto;

    img {
        border: none;
        background-color: transparent;
    }
`;

// Shortcut table styling
const ShortcutTable = styled.table`
    width: 100%;
    max-width: 500px;
    margin: 30px auto;
    border-collapse: collapse;
    font-size: 14px;
    text-align: left;

    th, td {
        border: 1px solid var(--grid-color);
        padding: 10px 12px;
        color: var(--text-muted);
    }

    th {
        background-color: rgba(255, 51, 153, 0.1);
        color: var(--primary-color);
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 13px;
    }

    tr:nth-child(even) {
        background-color: rgba(0, 0, 0, 0.2);
    }

    code {
        color: var(--primary-color);
        font-weight: bold;
        background-color: rgba(0, 0, 0, 0.3);
        padding: 2px 6px;
        border-radius: 3px;
    }
`;

// Capture modes grid - three columns for the three modes
const ModesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;
    margin-top: 40px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
`;

const ModeItem = styled.div`
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.3);
    padding: 25px;
    text-align: center;
    transition: border-color 0.3s ease, transform 0.3s ease;

    &:hover {
        border-color: var(--primary-color);
        transform: translateY(-3px);
    }
`;

const ModeTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 10px;
    text-transform: uppercase;
`;

const ModeDescription = styled.p`
    font-size: 14px;
    color: var(--text-muted);
    line-height: 1.5;
`;

// Stacked images container for zero-flash image swapping
const ModesImageStack = styled.div`
    position: relative;
    width: 100%;
`;

const StackedImage = styled(ResponsiveImage)`
    ${({ $visible }) => !$visible && `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        opacity: 0;
        pointer-events: none;
    `}
`;

// Minimal design points
const MinimalList = styled.ul`
    list-style: none;
    padding: 0;
    max-width: 600px;
    margin: 30px auto;
    text-align: center;

    li {
        font-size: 16px;
        color: var(--text-muted);
        margin-bottom: 15px;

        &::before {
            content: '—';
            color: var(--primary-color);
            margin-right: 10px;
        }
    }
`;

const DownloadDisclaimer = styled.p`
    font-size: 12px;
    color: #888;
    text-align: center;
    margin-top: 15px;
    max-width: 450px;
    margin-left: auto;
    margin-right: auto;

    a {
        color: var(--text-muted);
        text-decoration: underline;
        text-underline-offset: 2px;
        transition: color 0.2s ease;

        &:hover {
            color: var(--primary-color);
        }
    }
`;

const KissCamPage = () => {
    const kissCamProduct = getProduct('kisscam');
    const downloads = getProductDownloads('kisscam');
    const downloadUrl = downloads.macos;

    // Track which mode panel is being hovered to show corresponding frame
    const [hoveredMode, setHoveredMode] = useState(null);

    return (
        <ProjectWrapper>
            <PageMeta
                title="KissCam"
                description="KissCam - Fast, minimal screen capture for macOS. Screenshots and screen recording following the KISS principle: Keep It Simple, Screenshot."
                canonicalPath="/projects/kisscam"
            />
            {/* --- Hero Section --- */}
            <HeroSection>
                <LogoWrapper>
                    <ResponsiveImage
                        src={kissCamProduct.logo}
                        alt="KissCam Logo"
                        sizes="128px"
                    />
                </LogoWrapper>
                <PageTitle>KissCam</PageTitle>
                <SubHeadline style={{ fontStyle: 'italic', marginBottom: '10px' }}>
                    Keep It Simple, Screenshot
                </SubHeadline>
                <SubHeadline>
                    Fast, minimal screen capture for macOS. Screenshots and video recording without the bloat.
                </SubHeadline>
                <FigureWrapper style={{ marginTop: '20px', marginBottom: '30px' }}>
                    <ResponsiveImage
                        src="/assets/KissCam/img/hero.gif"
                        alt="KissCam capture overlay showing the clean, minimal interface"
                        sizes="(max-width: 800px) 100vw, 800px"
                    />
                </FigureWrapper>
                <StyledButton href={downloadUrl} download>
                    Download for macOS
                </StyledButton>
                <DownloadDisclaimer>
                    By downloading, you agree to the <Link to={kissCamProduct.termsPage}>Terms of Use</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
                </DownloadDisclaimer>
            </HeroSection>

            {/* --- The Why Section --- */}
            <Section>
                <SectionTitle>Why KissCam?</SectionTitle>
                <SubHeadline style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
                    I take a lot of screenshots. I record my screen constantly. I just wanted something that captures what I need, saves it, and gets out of the way.
                    <br /><br />
                    No account. No subscription. No fifteen features I'll never touch. Just press a shortcut, get a file, move on.
                </SubHeadline>
            </Section>

            {/* --- Capture Modes Section --- */}
            <Section>
                <SectionTitle>Three Ways to Capture</SectionTitle>
                <SubHeadline style={{ maxWidth: '800px', margin: '0 auto 20px auto' }}>
                    All three modes work for both screenshots and video recording.
                </SubHeadline>
                <ModesGrid>
                    <ModeItem
                        onMouseEnter={() => setHoveredMode('fullscreen')}
                        onMouseLeave={() => setHoveredMode(null)}
                    >
                        <ModeTitle>Full Screen</ModeTitle>
                        <ModeDescription>
                            Capture your entire display with a single click.
                        </ModeDescription>
                    </ModeItem>
                    <ModeItem
                        onMouseEnter={() => setHoveredMode('window')}
                        onMouseLeave={() => setHoveredMode(null)}
                    >
                        <ModeTitle>Window</ModeTitle>
                        <ModeDescription>
                            Click any window to capture it cleanly, without the clutter around it.
                        </ModeDescription>
                    </ModeItem>
                    <ModeItem
                        onMouseEnter={() => setHoveredMode('rectangle')}
                        onMouseLeave={() => setHoveredMode(null)}
                    >
                        <ModeTitle>Rectangle</ModeTitle>
                        <ModeDescription>
                            Draw a selection to capture exactly the area you need.
                        </ModeDescription>
                    </ModeItem>
                </ModesGrid>
                <FigureWrapper style={{ marginTop: '40px' }}>
                    <ModesImageStack>
                        <StackedImage
                            src="/assets/KissCam/img/modes.gif"
                            alt="KissCam capture modes: Full Screen, Window, and Rectangle selection"
                            sizes="(max-width: 800px) 100vw, 800px"
                            $visible={hoveredMode === null}
                        />
                        <StackedImage
                            src="/assets/KissCam/img/kisscamScreenMode.png"
                            alt="KissCam Full Screen capture mode"
                            sizes="(max-width: 800px) 100vw, 800px"
                            $visible={hoveredMode === 'fullscreen'}
                        />
                        <StackedImage
                            src="/assets/KissCam/img/kisscamWinMode.png"
                            alt="KissCam Window capture mode"
                            sizes="(max-width: 800px) 100vw, 800px"
                            $visible={hoveredMode === 'window'}
                        />
                        <StackedImage
                            src="/assets/KissCam/img/kisscamRectMode.png"
                            alt="KissCam Rectangle capture mode"
                            sizes="(max-width: 800px) 100vw, 800px"
                            $visible={hoveredMode === 'rectangle'}
                        />
                    </ModesImageStack>
                    <FigureCaption>
                        Choose your mode, capture, done.
                    </FigureCaption>
                </FigureWrapper>
            </Section>

            {/* --- Quick Capture Section --- */}
            <Section>
                <SectionTitle>Quick Capture // Zero Friction</SectionTitle>
                <SubHeadline style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'left' }}>
                    This is the feature I built KissCam for.
                    <br /><br />
                    Hit <Highlight>⌘⇧7</Highlight> for a screenshot. Hit <Highlight>⌘⇧8</Highlight> to start recording. No overlay, no extra clicks, no interruption. KissCam remembers your last settings: same mode, same region, same format. It just does it.
                    <br /><br />
                    Perfect for repetitive captures. Same window, same area, over and over. Press the shortcut, get the file.
                </SubHeadline>
                <FigureWrapper style={{ marginTop: '30px' }}>
                    <ResponsiveVideo
                        src="/assets/KissCam/vidz/quickCapture.mp4"
                        alt="Quick Capture in action: pressing a shortcut instantly captures without showing any overlay"
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls={false}
                        clickToPause={false}
                        linkToFullRes={true}
                    />
                    <FigureCaption>
                        Set up once. Then: shortcut pressed, thumbnail appears, file saved. Repeat endlessly.
                    </FigureCaption>
                </FigureWrapper>
            </Section>

            {/* --- Screenshots & Video Section --- */}
            <Section>
                <SectionTitle>Simple, Not Limited</SectionTitle>
                <SubHeadline style={{ maxWidth: '800px', margin: '0 auto 40px auto' }}>
                    A clean workflow doesn't mean fewer options. Pick your formats, configure your settings once, and capture exactly what you need.
                </SubHeadline>
                <FeaturesGrid>
                    <FeatureItem>
                        <FeatureTitle>Screenshots</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Multiple Formats:</Highlight> PNG, JPEG, WebP, HEIC, or TIFF.</FeatureListItem>
                            <FeatureListItem><Highlight>Auto Clipboard:</Highlight> Every capture is automatically copied, ready to paste.</FeatureListItem>
                            <FeatureListItem><Highlight>Thumbnail Preview:</Highlight> A small preview appears after each capture. Click to open, drag to move, swipe to dismiss.</FeatureListItem>
                        </FeatureList>
                    </FeatureItem>
                    <FeatureItem>
                        <FeatureTitle>Video Recording</FeatureTitle>
                        <FeatureList>
                            <FeatureListItem><Highlight>Containers:</Highlight> MOV or MP4.</FeatureListItem>
                            <FeatureListItem><Highlight>Codecs:</Highlight> H.264 or HEVC (H.265).</FeatureListItem>
                            <FeatureListItem><Highlight>Frame Rates:</Highlight> 30 fps or 60 fps.</FeatureListItem>
                            <FeatureListItem><Highlight>Audio:</Highlight> None, System Audio, Microphone, or Both (macOS 15+).</FeatureListItem>
                            <FeatureListItem><Highlight>Countdown:</Highlight> Optional 0-10 second countdown before recording starts.</FeatureListItem>
                        </FeatureList>
                    </FeatureItem>
                </FeaturesGrid>
            </Section>

            {/* --- Keyboard Shortcuts Section --- */}
            <Section>
                <SectionTitle>Keyboard Shortcuts</SectionTitle>
                <SubHeadline style={{ maxWidth: '800px', margin: '0 auto 20px auto' }}>
                    All shortcuts are customizable in Settings.
                </SubHeadline>
                <ShortcutTable>
                    <thead>
                        <tr>
                            <th>Shortcut</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td><code>⌘⇧6</code></td><td>Open capture overlay</td></tr>
                        <tr><td><code>⌘⇧7</code></td><td>Quick Capture (screenshot)</td></tr>
                        <tr><td><code>⌘⇧8</code></td><td>Quick Record (video)</td></tr>
                        <tr><td><code>Escape</code></td><td>Cancel capture</td></tr>
                    </tbody>
                </ShortcutTable>
            </Section>

            {/* --- Minimal by Design Section --- */}
            <Section>
                <SectionTitle>Minimal by Design</SectionTitle>
                <MinimalList>
                    <li>Lives in your menu bar, no dock icon clutter</li>
                    <li>Native macOS interface with Liquid Glass design</li>
                    <li>No bloat, no subscription, no cloud</li>
                </MinimalList>
            </Section>

            {/* --- System Requirements & Download --- */}
            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>Get KissCam</SectionTitle>
                <SubHeadline>
                    Screen capture that stays out of your way.
                </SubHeadline>
                <StyledButton href={downloadUrl} download>
                    Download for macOS
                </StyledButton>
                <DownloadDisclaimer>
                    By downloading, you agree to the <Link to={kissCamProduct.termsPage}>Terms of Use</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
                </DownloadDisclaimer>
                <TechSpecsList style={{ marginTop: '30px' }}>
                    <TechSpecItem><span>Requires</span><span>macOS 14.0 or later (Apple Silicon)</span></TechSpecItem>
                    <TechSpecItem><span>Combined Audio</span><span>macOS 15.0 (Sequoia) for System + Mic</span></TechSpecItem>
                    <TechSpecItem><span>Permissions</span><span>Screen Recording, Accessibility, Microphone (optional)</span></TechSpecItem>
                </TechSpecsList>
            </Section>
        </ProjectWrapper>
    );
};

export default KissCamPage;
